import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Spinner,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
} from "reactstrap";
import { AiOutlineEdit, AiOutlineEye } from "react-icons/ai";
import { FaCheck, FaTimes, FaToggleOn, FaToggleOff } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import Pagination from "../../Components/Paginate";
import Multiselect from "multiselect-react-dropdown";

const PartnerPlaces = () => {
  const {
    AuthReducer: { auth },
    PermissionReducer: { allowedPermissions },
  } = useSelector((state) => state);

  const [places, setPlaces] = useState([]);
  const [placesCount, setPlacesCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("desc");
  const [paginate, setPaginate] = useState(10);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [courseCategoryOption, setCourseCategoryOption] = useState([]);
  const [courseSubCategoryOption, setCourseSubCategoryOption] = useState([]);
  const [courseSubSubCategoryOption, setCourseSubSubCategoryOption] = useState([]);
  const [courseCategory, setCourseCategory] = useState([]);
  const [courseSubCategory, setCourseSubCategory] = useState([]);
  const [courseSubSubCategory, setCourseSubSubCategory] = useState([]);

  useEffect(() => {
    fetchPartnerPlaces();
    fetchCategories();
  }, [auth, paginate, page, search, sort]);

  const fetchPartnerPlaces = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/admin/v1/partner-places", {
        headers: { Authorization: `Bearer ${auth?.token}` },
        params: { page: page + 1, limit: paginate, search, sort },
      });
      setPlaces(response.data.places || []);
      setPlacesCount(response.data.totalCount || 0);
    } catch (error) {
      toast.error("Failed to fetch partner places");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get("/api/admin/v1/categories", {
        headers: { Authorization: `Bearer ${auth?.token}` },
      });
      setCourseCategoryOption(
        response.data.categories.map((item) => ({ name: item.name, id: item.id }))
      );
    } catch (error) {
      console.error("Failed to fetch categories");
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await axios.put(
        `/api/admin/v1/partner-place-toggle/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${auth?.token}` },
        }
      );
      toast.success(response.data.message || "Status updated successfully");
      fetchPartnerPlaces();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleApprove = async (id) => {
    if (window.confirm("Are you sure you want to approve this place?")) {
      try {
        const response = await axios.put(
          `/api/admin/v1/partner-place-toggle/${id}`,
          { status: "approved" },
          { headers: { Authorization: `Bearer ${auth?.token}` } }
        );
        toast.success("Place approved successfully");
        fetchPartnerPlaces();
      } catch (error) {
        toast.error("Failed to approve place");
      }
    }
  };

  const handleReject = async (id) => {
    if (window.confirm("Are you sure you want to reject this place?")) {
      try {
        const response = await axios.put(
          `/api/admin/v1/partner-place-toggle/${id}`,
          { status: "rejected" },
          { headers: { Authorization: `Bearer ${auth?.token}` } }
        );
        toast.success("Place rejected successfully");
        fetchPartnerPlaces();
      } catch (error) {
        toast.error("Failed to reject place");
      }
    }
  };

  const openEditModal = (place) => {
    setSelectedPlace(place);
    
    // Pre-populate existing categories
    const existingCategories = place.place_category_ids?.map(cat => ({
      id: cat.category?.id,
      name: cat.category?.name
    })).filter(cat => cat.id && cat.name) || [];
    
    const existingSubCategories = place.place_sub_category_ids?.map(sub => ({
      id: sub.sub_category?.id,
      name: sub.sub_category?.title_english,
      category_id: sub.sub_category?.category_id
    })).filter(sub => sub.id && sub.name) || [];
    
    const existingSubSubCategories = place.place_sub_sub_category_ids?.map(subSub => ({
      id: subSub.sub_sub_category?.id,
      name: subSub.sub_sub_category?.title_english,
      category_id: subSub.sub_sub_category?.category_id,
      sub_category_id: subSub.sub_sub_category?.sub_category_id
    })).filter(subSub => subSub.id && subSub.name) || [];
    
    setCourseCategory(existingCategories);
    setCourseSubCategory(existingSubCategories);
    setCourseSubSubCategory(existingSubSubCategories);
    setCourseSubCategoryOption([]);
    setCourseSubSubCategoryOption([]);
    
    // Load subcategories for existing categories
    if (existingCategories.length > 0) {
      handleCourseCategoryChange(existingCategories);
    }
    
    setEditModal(true);
    console.log('Categories:', place.place_category_ids?.map(cat => cat.Place_category_id));
    console.log('Sub Categories:', place.place_sub_category_ids?.map(sub => sub.place_sub_category_id));
    console.log('Sub Sub Categories:', place.place_sub_sub_category_ids?.map(subSub => subSub.place_sub_sub_category_id));
  };

  const handleCourseCategoryChange = (val) => {
    if (val.length) {
      axios.get(`/api/admin/v1/sub-category-list?id=${val.map(it => it.id).join(',')}`, {
        headers: {
          Authorization: `Bearer ${auth.token}`
        }
      })
        .then((({ data }) => {
          setCourseSubCategory(prev => prev.filter((item) => val.find(it => it.id === item.id)))
          setCourseSubCategoryOption(data.data.map((item) => {
            return { name: item.title_english, id: item.id, category_id: item.category_id }
          }))
        }))
        .catch((err) => console.log(err))
    } else {
      setCourseSubCategory([])
      setCourseSubCategoryOption([]);
    }
  }

  const handleCourseSubCategoryChange = (val) => {
    if (val.length) {
      axios.get(`/api/admin/v1/sub-sub-category-list?category_id=${val.map(item => item.category_id).join(',')}&sub_category_id=${val.map(it => it.id).join(',')}`, {
        headers: {
          Authorization: `Bearer ${auth.token}`
        }
      })
        .then((({ data }) => setCourseSubSubCategoryOption(data.data.map((item) => {
          return { name: item.title_english, id: item.id, category_id: item.category_id, sub_category_id: item.sub_category_id }
        }))))
        .catch((err) => console.log(err))
    } else {
      setCourseSubSubCategory([])
      setCourseSubSubCategoryOption([])
    }
  }

  const handleUpdateCategories = async () => {
    try {
      const form = new FormData();
      form.append('category_id', JSON.stringify(courseCategory));
      form.append('sub_category_id', JSON.stringify(courseSubCategory));
      form.append('sub_sub_category_id', JSON.stringify(courseSubSubCategory));
      
      const response = await axios.put(
        `/api/admin/v1/partner-place-categories/${selectedPlace.id}`,
        form,
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      toast.success("Categories updated successfully");
      setEditModal(false);
      fetchPartnerPlaces();
    } catch (error) {
      toast.error("Failed to update categories");
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      approved: "success",
      rejected: "danger",
      pending: "warning",
    };
    const statusStr = String(status || "unknown").toLowerCase();
    return (
      <span className={`badge bg-${statusColors[statusStr] || "secondary"}`}>
        {statusStr.charAt(0).toUpperCase() + statusStr.slice(1)}
      </span>
    );
  };

  return (
    <div>
      <div className="row my-3">
        <div className="col-md-12">
          <div className="mb-4 d-flex align-items-center justify-content-between">
            <h5 className="mb-0 text-white fs-4">Partner Places</h5>
          </div>
        </div>
      </div>

      <div
        className="bg-white px-3 py-4 rounded-2 my-4"
        style={{ boxShadow: "4px 4px 18px rgba(0,0,0,0.15)" }}
      >
        <div className="row">
          <div className="col-md-3 mb-2">
            <Input
              size="sm"
              value={search}
              placeholder="Search By Place Name"
              onChange={({ target: { value } }) => setSearch(value)}
            />
          </div>
          <div className="col-md-2 offset-md-7">
            <Input
              type="select"
              size="sm"
              value={sort}
              onChange={({ target }) => setSort(target.value)}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </Input>
          </div>
          <div className="col-md-12">
            <Table hover responsive>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Place Name</th>
                  <th>Partner Name</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Sub Sub Category</th>
                  <th>Active</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : places.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  places.map((place, index) => (
                    <tr key={place.id}>
                      <th scope="row">{index + 1 + page * paginate}</th>
                      <td>{place.place_name}</td>
                      <td>{place.partner?.name}</td>
                      <td>
                        {place.place_category_ids?.map(cat => cat.category?.name).join(", ") || "-"}
                      </td>
                      <td>
                        {place.place_sub_category_ids?.map(sub => sub.sub_category?.title_english).join(", ") || "-"}
                      </td>
                      <td>
                        {place.place_sub_sub_category_ids?.map(subSub => subSub.sub_sub_category?.title_english).join(", ") || "-"}
                      </td>
                      <td>
                        <Button
                          color={
                            place.status === 1 ? "success" : "secondary"
                          }
                          size="sm"
                          onClick={() => handleToggleStatus(place.id)}
                        >
                          {place.status === 1 ? (
                            <FaToggleOn />
                          ) : (
                            <FaToggleOff />
                          )}
                        </Button>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          <Button
                            color="success"
                            size="sm"
                            className="me-2"
                            onClick={() => handleApprove(place.id)}
                            disabled={place.status === "approved"}
                          >
                            <FaCheck />
                          </Button>
                          <Button
                            color="danger"
                            size="sm"
                            className="me-2"
                            onClick={() => handleReject(place.id)}
                            disabled={place.status === "rejected"}
                          >
                            <FaTimes />
                          </Button>
                          <Button
                            color="warning"
                            size="sm"
                            onClick={() => openEditModal(place)}
                          >
                            <AiOutlineEdit />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <div className="row align-items-center">
          <div className="col-md-6 d-flex">
            <div className="col-md-2">
              <Input
                type="select"
                id="paginate"
                onChange={(e) => setPaginate(e.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Input>
            </div>
          </div>
          <div className="col-md-6">
            <Pagination
              totalItems={placesCount}
              itemsPerPage={paginate}
              currentPage={page}
              onPageChange={setPage}
            />
          </div>
        </div>
      </div>

      {/* Edit Categories Modal */}
      <Modal
        isOpen={editModal}
        toggle={() => setEditModal(!editModal)}
        size="lg"
      >
        <ModalHeader toggle={() => setEditModal(!editModal)}>
          Update Categories - {selectedPlace?.place_name}
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-md-12 mb-3">
              <FormGroup>
                <Label>Category</Label>
                <Multiselect className="text-black"
                  options={courseCategoryOption}
                  displayValue="name"
                  selectedValues={courseCategory}
                  onSelect={(val) => {
                    setCourseCategory([...val])
                    handleCourseCategoryChange(val)
                  }}
                  onRemove={(val => {
                    setCourseCategory([...val])
                    handleCourseCategoryChange(val)
                  })}
                  labelledBy="Select"
                />
              </FormGroup>
            </div>
            <div className="col-md-12 mb-3">
              <FormGroup>
                <Label>Sub Category</Label>
                <Multiselect className="text-black"
                  options={courseSubCategoryOption}
                  displayValue="name"
                  selectedValues={courseSubCategory}
                  onSelect={(val) => {
                    setCourseSubCategory([...val])
                    handleCourseSubCategoryChange(val)
                  }}
                  onRemove={(val => {
                    setCourseSubCategory([...val])
                    handleCourseSubCategoryChange(val)
                  })}
                  labelledBy="Select"
                />
              </FormGroup>
            </div>
            <div className="col-md-12 mb-3">
              <FormGroup>
                <Label>Sub Sub Category</Label>
                <Multiselect className="text-black"
                  options={courseSubSubCategoryOption}
                  selectedValues={courseSubSubCategory}
                  displayValue="name"
                  onSelect={(val) => {
                    setCourseSubSubCategory(val)
                  }}
                  onRemove={(val) => setCourseSubSubCategory([...val])}
                  labelledBy="Select"
                />
              </FormGroup>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleUpdateCategories}>
            Update Categories
          </Button>
          <Button color="secondary" onClick={() => setEditModal(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PartnerPlaces;